import React from 'react';
import { Plus, Package, Shield } from 'lucide-react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Button,
  Badge,
} from 'lift-ui';
import { Product, PermissionLevel } from '../types';
import '../iam-support.styles.scss';

interface ProductsSectionProps {
  products: Product[];
  permissionLevels?: PermissionLevel[];
  onAddProduct: () => void;
}

const ProductsSection: React.FC<ProductsSectionProps> = ({ 
  products, 
  permissionLevels = [],
  onAddProduct 
}) => {
  return (
    <div className="iam-support-products">
      <div className="iam-support-section-header">
        <h2>Supported Products</h2>
      </div>

      {/* Permission Levels Card */}
      <Card shadow="medium" className="iam-support-products-card mb-6">
        <CardHeader showIcon={true} icon={<Shield className="h-5 w-5 text-blue-600" />} showMoreIcon={false}>
          <CardTitle>Permission Levels</CardTitle>
          <CardDescription>
            Available permission levels and their weights (higher weight = more access)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            {permissionLevels.length > 0 ? (
              permissionLevels.map((level) => (
                <div 
                  key={level.level_id} 
                  className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border"
                >
                  <Badge 
                    variant={
                      level.level_name === 'Manager' ? 'default' : 
                      level.level_name === 'Editor' ? 'secondary' : 'outline'
                    }
                    className="text-sm"
                  >
                    {level.level_name}
                  </Badge>
                </div>
              ))
            ) : (
              <div className="text-gray-500 text-sm">
                <p>Default permission levels:</p>
                <div className="flex gap-4 mt-2">
                  <Badge variant="outline">Viewer (10)</Badge>
                  <Badge variant="secondary">Editor (20)</Badge>
                  <Badge variant="default">Manager (30)</Badge>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Products Grid Card */}
      <Card shadow="medium" className="iam-support-products-card">
        <CardHeader showIcon={true} icon={<Package className="h-5 w-5 text-green-600" />} showMoreIcon={false}>
          <CardTitle>Available Products</CardTitle>
          <CardDescription>
            Products/functionalities that can have permissions assigned
          </CardDescription>
        </CardHeader>
        <CardContent>
          {products.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
              {products.map((product) => (
                <div 
                  key={product.product_id} 
                  className="p-3 bg-gray-50 rounded-lg border hover:border-blue-300 hover:bg-blue-50 transition-colors"
                >
                  <div className="font-medium text-gray-900">
                    {product.product_name}
                  </div>
                  <div className="text-xs text-gray-500 mt-1 font-mono">
                    {product.product_key}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Package className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>No products configured yet.</p>
              <p className="text-sm mt-1">Add products to start assigning permissions.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ProductsSection;
